import { type ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { UserNameColumn } from "@/components/users/user-name-column";
import { StatusPill, PriorityPill } from "../pills";
import { DirectionPill } from "./direction-pill";
import type { UserReport } from "./types";

export function createUserReportColumns(): ColumnDef<UserReport>[] {
  return [
    {
      id: "direction",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Direction"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => <DirectionPill direction={row.original.direction} />,
    },
    {
      id: "reporter",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Reporter"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => {
        const { reporter } = row.original;
        return (
          <UserNameColumn
            profilePhoto={reporter?.profile_picture_url ?? ""}
            name={reporter?.name ?? "Unknown"}
            researchConsent={false}
            redactedFields={reporter?.redacted_fields}
            userId={reporter?.id}
          />
        );
      },
    },
    {
      id: "reported",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Reported"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => {
        const { reportedUser } = row.original;
        return (
          <UserNameColumn
            profilePhoto={reportedUser?.profile_picture_url ?? ""}
            name={reportedUser?.name ?? "Unknown"}
            researchConsent={false}
            redactedFields={reportedUser?.redacted_fields}
            userId={reportedUser?.id}
          />
        );
      },
    },
    {
      id: "time",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Time"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="w-[140px] whitespace-nowrap text-sm text-zinc-500">
          {formatDistanceToNow(new Date(row.original.createdAt), {
            addSuffix: true,
          }).replace(/^about /, "")}
        </span>
      ),
    },
    {
      id: "category",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Category"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => (
        <span className="text-sm text-zinc-600">
          {row.original.categoryLabel ?? (
            <span className="italic text-zinc-400">No category</span>
          )}
        </span>
      ),
    },
    {
      id: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => <StatusPill status={row.original.status} />,
    },
    {
      id: "tier",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tier"
          className="justify-start"
        />
      ),
      enableSorting: false,
      cell: ({ row }) => <PriorityPill priority={row.original.priority} />,
    },
  ];
}
